import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { ErrorText, Panel } from "../../components/ui/feedback";
import { useI18n } from "../../i18n/I18nProvider";
import type { LinkCode } from "../../lib/api-types";
import {
  LAST_MINUTE_MS,
  TICK_MS,
  formatCountdown,
  linkCodeDisplay,
  remainingFraction,
} from "./countdown";

export interface LinkCodePanelProps {
  link: LinkCode;
  busy: boolean;
  error?: unknown;
  onNewCode: () => void;
  onDone: () => void;
}

/**
 * The link code in jersey numerals, with a rink-length countdown that drains and turns red in the
 * last minute. The status line is the only live region, so only state changes are announced.
 * The parent keys this by the code, so each new code starts a fresh clock.
 */
export function LinkCodePanel({ link, busy, error, onNewCode, onDone }: LinkCodePanelProps) {
  const { t } = useI18n();
  const expiresAt = new Date(link.expires_at).getTime();
  const [now, setNow] = useState(() => Date.now());
  const [windowMs] = useState(() => Math.max(1, expiresAt - Date.now()));
  const remaining = Math.max(0, expiresAt - now);
  const expired = remaining === 0;
  const lastMinute = !expired && remaining <= LAST_MINUTE_MS;

  // Tick once a second until the code expires. The interval is cleared on expiry or unmount.
  useEffect(() => {
    if (expired) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [expired]);

  const statusText = expired
    ? t("devices.codeExpired")
    : lastMinute
      ? t("devices.codeLastMinute")
      : t("devices.codeReady");

  return (
    <Panel className="space-y-6 md:p-7">
      <div className="space-y-2">
        <h2 className="font-display text-xl font-extrabold tracking-[0.01em] text-ink uppercase">
          {t("devices.codeTitle")}
        </h2>
        <p className="text-note text-ink-2">{t("devices.codeSteps")}</p>
      </div>

      <p
        className={`text-center font-display text-[3.25rem] leading-[0.92] font-extrabold tracking-[0.12em] tabular-nums sm:text-display-xl ${
          expired ? "text-ink-3 line-through" : "text-ink"
        }`}
      >
        {linkCodeDisplay(link.code)}
      </p>

      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <div aria-hidden="true" className="relative h-3 flex-1 rounded-sm bg-ice-2">
            <div
              className={`absolute inset-y-0 left-0 rounded-sm transition-[width] duration-1000 ease-linear motion-reduce:transition-none ${
                lastMinute ? "bg-red" : "bg-blue"
              }`}
              style={{ width: `${remainingFraction(remaining, windowMs) * 100}%` }}
            />
          </div>
          {expired ? null : (
            <p className="shrink-0 text-note font-semibold tabular-nums text-ink-2">
              {t("devices.countdown", { time: formatCountdown(remaining) })}
            </p>
          )}
        </div>
        <p
          role="status"
          aria-live="polite"
          className={`text-note font-semibold ${lastMinute ? "text-red" : "text-ink"}`}
        >
          {statusText}
        </p>
      </div>

      <ErrorText error={error} />
      <div className="flex flex-wrap gap-2">
        <Button variant={expired ? "primary" : "secondary"} loading={busy} onClick={onNewCode}>
          {t("devices.newCode")}
        </Button>
        <Button variant={expired ? "secondary" : "primary"} onClick={onDone}>
          {t("devices.done")}
        </Button>
      </div>
    </Panel>
  );
}
